/**
 * The sections of the usage page, and the only place their names are written.
 *
 * The breadcrumb needs the default section's name for the frame between mount
 * and the page's first publish. Spelling it there as well let a renamed section
 * drift into a crumb label the page never shows, so both read this instead.
 */
export const USAGE_TABS = [
  { id: "provider-usage", label: "Connected subscriptions" },
  { id: "estimate", label: "Estimated value" },
] as const;

export type UsageTabId = (typeof USAGE_TABS)[number]["id"];

export function isUsageTabId(value: unknown): value is UsageTabId {
  return USAGE_TABS.some((tab) => tab.id === value);
}

/**
 * The section the page opens on when the URL does not name one.
 *
 * A bookmark to the retired `plan` section lands here rather than breaking: the
 * route validates `?tab=` through {@link isUsageTabId}, so an id this list no
 * longer carries is dropped and the default takes over.
 */
export const DEFAULT_USAGE_TAB: UsageTabId = "provider-usage";

/**
 * The section an untrusted value names, or the default when it names none.
 *
 * `?tab=` carries whatever a stale bookmark or a hand-edited address bar says,
 * and retiring a section turns every existing link to it into exactly that.
 * Callers used to coalesce on absence alone (`tab ?? DEFAULT`), which passes a
 * present-but-unknown id straight through to a switch that has no arm for it,
 * so the page drew its frame with nothing inside. Resolving here keeps the
 * section list and the fallback rule in one place.
 */
export function resolveUsageTab(value: unknown): UsageTabId {
  return isUsageTabId(value) ? value : DEFAULT_USAGE_TAB;
}
